import React, { useState, useRef, useEffect } from 'react'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import Collapse from '@mui/material/Collapse'
import CircularProgress from '@mui/material/CircularProgress'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import EditIcon from '@mui/icons-material/Edit'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import AddIcon from '@mui/icons-material/Add'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import { DndContext, DragEndEvent, PointerSensor, useSensor, useSensors, closestCenter } from '@dnd-kit/core'
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { restrictToVerticalAxis, restrictToParentElement } from '@dnd-kit/modifiers'
import { TppSection, TppRow } from '../../types'
import RowListItem from './RowListItem'

interface SectionListItemProps {
    section: TppSection
    rows: TppRow[]
    onUpdateSectionTitle: (sectionId: string, title: string) => Promise<void>
    onDeleteSection: (section: TppSection) => void
    onAddRow: (sectionId: string) => void
    onUpdateRowTitle: (rowId: string, title: string) => Promise<void>
    onDeleteRow: (row: TppRow) => void
    onReorderRows: (sectionId: string, rowIds: string[]) => void
    isUpdatingSection: boolean
    isUpdatingRow: boolean
    isAddingRow: boolean
}

const SectionListItem = ({
    section,
    rows,
    onUpdateSectionTitle,
    onDeleteSection,
    onAddRow,
    onUpdateRowTitle,
    onDeleteRow,
    onReorderRows,
    isUpdatingSection,
    isUpdatingRow,
    isAddingRow,
}: SectionListItemProps) => {
    const [isEditing, setIsEditing] = useState(false)
    const [editValue, setEditValue] = useState(section.title)
    const [isExpanded, setIsExpanded] = useState(false)
    const inputRef = useRef<HTMLInputElement>(null)

    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: section._id,
    })
    const dragStyle: React.CSSProperties = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.6 : 1,
    }

    const rowSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }))

    useEffect(() => {
        if (isEditing && inputRef.current) {
            inputRef.current.focus()
            inputRef.current.select()
        }
    }, [isEditing])

    const handleStartEdit = () => {
        setEditValue(section.title)
        setIsEditing(true)
    }

    const handleCancelEdit = () => {
        setEditValue(section.title)
        setIsEditing(false)
    }

    const handleSaveEdit = async () => {
        if (editValue.trim() && editValue !== section.title) {
            await onUpdateSectionTitle(section._id, editValue.trim())
        }
        setIsEditing(false)
    }

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleSaveEdit()
        } else if (e.key === 'Escape') {
            handleCancelEdit()
        }
    }

    const sectionRows = section.rowIds
        .map((id) => rows.find((r) => r._id === id))
        .filter((r): r is TppRow => Boolean(r))

    const handleRowDragEnd = (event: DragEndEvent) => {
        const { active, over } = event
        if (!over || active.id === over.id) return
        const ids = sectionRows.map((r) => r._id)
        const oldIndex = ids.indexOf(active.id as string)
        const newIndex = ids.indexOf(over.id as string)
        if (oldIndex === -1 || newIndex === -1) return
        onReorderRows(section._id, arrayMove(ids, oldIndex, newIndex))
    }

    return (
        <Stack ref={setNodeRef} style={dragStyle}>
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                py={1.5}
                px={2}
                sx={{
                    borderRadius: '8px',
                    bgcolor: 'figma.grayscale.tier6',
                    '&:hover': {
                        bgcolor: 'figma.grayscale.tier5',
                    },
                }}
            >
                {isEditing ? (
                    <Stack direction="row" alignItems="center" gap={1} flex={1}>
                        <TextField
                            inputRef={inputRef}
                            size="small"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            onKeyDown={handleKeyDown}
                            disabled={isUpdatingSection}
                            sx={{ flex: 1 }}
                        />
                        <IconButton
                            size="small"
                            onClick={handleSaveEdit}
                            disabled={isUpdatingSection || !editValue.trim()}
                        >
                            {isUpdatingSection ? (
                                <CircularProgress size={18} />
                            ) : (
                                <CheckIcon fontSize="small" color="success" />
                            )}
                        </IconButton>
                        <IconButton size="small" onClick={handleCancelEdit} disabled={isUpdatingSection}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Stack>
                ) : (
                    <>
                        <Stack direction="row" alignItems="center" gap={1}>
                            <IconButton
                                size="small"
                                {...attributes}
                                {...listeners}
                                sx={{ cursor: 'grab', color: 'figma.grayscale.tier2', touchAction: 'none' }}
                                aria-label="Drag section"
                            >
                                <DragIndicatorIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" onClick={() => setIsExpanded(!isExpanded)}>
                                {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                            <Typography variant="16x600" color="figma.grayscale.textHier1">
                                {section.title}
                            </Typography>
                            <Typography variant="14x400" color="figma.grayscale.textHier3">
                                ({sectionRows.length} rows)
                            </Typography>
                        </Stack>
                        <Stack direction="row" gap={0.5}>
                            <IconButton size="small" onClick={handleStartEdit} sx={{ color: 'figma.grayscale.tier2' }}>
                                <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                                size="small"
                                onClick={() => onDeleteSection(section)}
                                sx={{ color: 'figma.grayscale.tier2', '&:hover': { color: 'error.main' } }}
                            >
                                <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                        </Stack>
                    </>
                )}
            </Stack>

            <Collapse in={isExpanded}>
                <Stack py={1} gap={0.5}>
                    <DndContext
                        sensors={rowSensors}
                        collisionDetection={closestCenter}
                        modifiers={[restrictToVerticalAxis, restrictToParentElement]}
                        onDragEnd={handleRowDragEnd}
                    >
                        <SortableContext items={sectionRows.map((r) => r._id)} strategy={verticalListSortingStrategy}>
                            {sectionRows.map((row) => (
                                <RowListItem
                                    key={row._id}
                                    row={row}
                                    onUpdateTitle={onUpdateRowTitle}
                                    onDelete={onDeleteRow}
                                    isUpdating={isUpdatingRow}
                                />
                            ))}
                        </SortableContext>
                    </DndContext>
                    <Button
                        startIcon={isAddingRow ? <CircularProgress size={16} /> : <AddIcon />}
                        onClick={() => onAddRow(section._id)}
                        disabled={isAddingRow}
                        sx={{
                            ml: 4,
                            justifyContent: 'flex-start',
                            color: 'figma.grayscale.textHier3',
                            '&:hover': {
                                color: 'figma.primary',
                                bgcolor: 'transparent',
                            },
                        }}
                    >
                        Add Row
                    </Button>
                </Stack>
            </Collapse>
        </Stack>
    )
}

export default SectionListItem
